// Funções puras da entrega ao vivo. O status vem do betracking, que decide
// sozinho pela posição do motorista (Traccar) — aqui só se traduz pra tela.

export const ETAPAS = [
  { status: 'scheduled', label: 'Agendada' },
  { status: 'en_route', label: 'A caminho' },
  { status: 'arriving', label: 'Chegou ao destino' },
  { status: 'delivered', label: 'Entregue' }
]

// A coleta existe no betracking (é o GPS que decide quando o motorista sai
// dela), mas pro cliente a entrega só "começa" quando sai pro endereço dele:
// enquanto coleta, continua Agendada.
const naTela = (status) => (status === 'collecting' ? 'scheduled' : status)

export const etapaAtual = (status) => ETAPAS.findIndex((e) => e.status === naTela(status))

// "Experiência #8168", nunca "Pedido" (o betracking guarda o rótulo do
// cadastro): vale pro cliente e pro motorista.
export const nomeParaCliente = (rotulo) => String(rotulo || '').replace(/^(pedido|experiencia)\b/i, 'Experiência')

// Pra onde vai: o pedido vai pra empresa; o envio múltiplo, pra casa de quem
// ganha o presente; o envio de lote que sai inteiro pra empresa (ex.: o lote
// grande demais da Ploomes, 20/10/2026) volta a ser "empresa" — no pino do
// mapa e no texto ("Sua entrega", não "Seu presente").
export const destinoEhEmpresa = (view) => view?.sourceType !== 'shipment' || view?.destinoEmpresa === true

// Dia da entrega agendada, em Brasília: "hoje", "amanhã" ou "terça, 30/09".
// scheduledDate vem como "2026-09-30" (dia, sem hora).
const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']
export const diaEmBrasilia = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d)
export const diaDaEntrega = (scheduledDate, agora = new Date()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduledDate || '')) return null
  const hoje = diaEmBrasilia(agora)
  const amanha = diaEmBrasilia(new Date(agora.getTime() + 24 * 3600 * 1000))
  if (scheduledDate === hoje) return 'hoje'
  if (scheduledDate === amanha) return 'amanhã'
  const [a, m, d] = scheduledDate.split('-').map(Number)
  return `${DIAS[new Date(Date.UTC(a, m - 1, d)).getUTCDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`
}

// Agendada com data: "Agendada · 30/09" (hoje: só "Agendada").
export const rotuloDoStatus = (view) => {
  const rotulo = ETAPAS.find((e) => e.status === naTela(view.status))?.label ?? ''
  if (naTela(view.status) !== 'scheduled') return rotulo
  const dia = diaDaEntrega(view.scheduledDate)
  return dia && dia !== 'hoje' ? `${rotulo} · ${dia === 'amanhã' ? 'amanhã' : dia.split(', ')[1]}` : rotulo
}

export const mostraMapa = (view) => Boolean(view?.position) && ['en_route', 'arriving'].includes(view.status)

// Antes de o motorista sair: mapa PARADO, com o destino e a caixinha da beuni
// esperando num ponto fixo no centro de São Paulo (Praça da Sé), não no CD —
// só mostra que a entrega está pra sair, sem revelar endereço nem fingir GPS.
// O destino vem do betracking desde o agendamento. Vale pro card do pedido e
// pro link público.
export const PONTO_DE_ESPERA = { lat: -23.5503, lng: -46.6339 }
export const mostraMapaParado = (view) =>
  !mostraMapa(view) &&
  ['scheduled', 'collecting'].includes(view?.status) &&
  Number.isFinite(view?.destination?.lat) &&
  Number.isFinite(view?.destination?.lng)

export const haQuanto = (iso, agora = Date.now()) => {
  const s = Math.max(0, Math.round((agora - Date.parse(iso)) / 1000))
  if (s < 60) return `há ${s} s`
  const min = Math.round(s / 60)
  if (min < 60) return `há ${min} min`
  return `há ${Math.round(min / 60)} h`
}

export const sinalFraco = (iso, agora = Date.now()) => agora - Date.parse(iso) > 2 * 60 * 1000

export const distanciaTexto = (metros) =>
  metros < 1000 ? `${Math.round(metros / 10) * 10} m` : `${(metros / 1000).toFixed(1).replace('.', ',')} km`

const rad = (g) => (g * Math.PI) / 180
export const distanciaMetros = (a, b) => {
  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.sqrt(h))
}

export const interpolar = (de, para, k) => ({
  lat: de.lat + (para.lat - de.lat) * k,
  lng: de.lng + (para.lng - de.lng) * k
})

// Aviso embaixo do mapa (pedido e link público): a caixinha é a posição que o
// celular do motorista informa, que pode errar alguns metros (prédio alto,
// garagem, túnel).
export const AVISO_GPS = '📍 Posição aproximada, pelo GPS do celular do motorista. Pode variar alguns metros.'

// ENTREGA COM VÁRIOS PEDIDOS (Murilo, 30/09/2026): o betracking marca os
// pedidos da mesma empresa, mesmo dia e mesmo endereço com o mesmo
// `entrega.codigo`. Na tela do motorista eles viram UM item: começa uma vez e
// finaliza uma vez (uma nota por pedido). Pedido com envio guardado no celular
// (ou recusado) fica de fora e aparece sozinho, como antes: não mistura estados.
// Código da entrega ("K7Q2M", aleatório: não revela quantas entregas a beuni
// já fez). `numero` é do betracking antigo, enquanto a troca sobe.
export const codigoDaEntrega = (e) => e?.codigo ?? e?.entrega?.codigo ?? e?.entrega?.numero ?? null
const ORDEM_DO_STATUS = ['scheduled', 'collecting', 'en_route', 'arriving', 'delivered']
export const juntarEntregas = (entregas, fila = []) => {
  const naFila = new Set(fila.map((f) => f.entregaId))
  const grupos = new Map()
  const saida = []
  for (const e of entregas || []) {
    const codigo = codigoDaEntrega(e)
    const juntos = codigo != null && (e.entrega?.pedidos || []).length > 1 && !naFila.has(e.id)
    if (!juntos) {
      saida.push(e)
      continue
    }
    if (!grupos.has(codigo)) {
      const grupo = { id: `entrega-${codigo}`, codigo, pedidos: [] }
      grupos.set(codigo, grupo)
      saida.push(grupo)
    }
    grupos.get(codigo).pedidos.push(e)
  }
  return saida
    .map((item) => {
      if (!item.pedidos) return item
      // Sobrou um só (o outro está na fila): volta a ser um pedido comum.
      if (item.pedidos.length === 1) return item.pedidos[0]
      const abertos = item.pedidos.filter((p) => p.status !== 'delivered')
      const base = abertos[0] || item.pedidos[0]
      // A entrega anda junto: vale o pedido mais adiantado ainda aberto. Se
      // algum ainda nem começou, a entrega fica "a começar": o arraste começa
      // os que faltam (sem isso, o pedido agendado nunca receberia o começar).
      const naoComecados = abertos.filter((p) => ['scheduled', 'collecting'].includes(p.status))
      const status = naoComecados.length
        ? naoComecados[0].status
        : abertos.length
          ? abertos.map((p) => p.status).sort((a, b) => ORDEM_DO_STATUS.indexOf(b) - ORDEM_DO_STATUS.indexOf(a))[0]
          : 'delivered'
      return {
        ...base,
        id: item.id,
        codigo: item.codigo,
        pedidos: item.pedidos,
        pedido: `Entrega #${item.codigo}`,
        status,
        entregueEm: abertos.length ? null : item.pedidos.map((p) => p.entregueEm).filter(Boolean).sort().pop() || null
      }
    })
}
